
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/base/Button';
import SEO from '../../components/SEO';

export default function Software() {
  const [activeService, setActiveService] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const philosophicalQuestions = [
    "What if your idea could change the world?",
    "Can technology truly solve humanity's greatest challenges?",
    "What happens when vision meets execution?",
    "How do you build the impossible?",
    "What if your next project defines the future?"
  ];

  const services = [
    {
      title: "Digital Product Revolution",
      description: "We don't just build apps — we architect digital experiences that redefine industries",
      features: ["Revolutionary React & React Native", "Scalable Node.js & Python backends", "Cloud-native architecture mastery", "Progressive Web App innovation"],
      icon: "ri-smartphone-line",
      gradient: "from-purple-500 to-pink-600",
      intensity: "Game-Changing"
    },
    {
      title: "Experience Design Mastery",
      description: "Beautiful interfaces that don't just look good — they convert minds and capture hearts",
      features: ["Psychology-driven user research", "Brand identity that dominates", "Conversion-optimized design systems", "Emotional design architecture"],
      icon: "ri-palette-line",
      gradient: "from-blue-500 to-cyan-600",
      intensity: "Mind-Bending"
    },
    {
      title: "Infrastructure Domination",
      description: "Build systems that don't just scale — they evolve, adapt, and never break",
      features: ["AWS & Azure mastery", "Self-healing CI/CD pipelines", "Real-time monitoring & analytics", "Fort Knox-level security"],
      icon: "ri-cloud-line",
      gradient: "from-green-500 to-emerald-600",
      intensity: "Bulletproof"
    },
    {
      title: "Integration Wizardry",
      description: "Connect everything to everything. Make systems talk, dance, and work in perfect harmony",
      features: ["API architecture perfection", "Custom integration solutions", "Database optimization mastery", "Performance monitoring excellence"],
      icon: "ri-links-line",
      gradient: "from-orange-500 to-red-600",
      intensity: "Seamless"
    },
    {
      title: "Digital Transformation",
      description: "Transform organizations from the inside out. Build the future, one team at a time",
      features: ["Revolutionary training programs", "Strategic digital consulting", "Team transformation workshops", "Future-ready technology adoption"],
      icon: "ri-graduation-cap-line",
      gradient: "from-indigo-500 to-purple-600",
      intensity: "Transformative"
    }
  ];

  const caseStudies = [
    {
      title: "E-commerce Revolution",
      client: "Retail Disruptor",
      result: "500% revenue explosion",
      impact: "Market domination achieved",
      image: "https://readdy.ai/api/search-image?query=Futuristic%20African%20e-commerce%20platform%20with%20holographic%20product%20displays%2C%20revolutionary%20purple%20and%20blue%20interface%20design%2C%20cyberpunk%20shopping%20experience%2C%20advanced%20tech%20workspace%20with%20neon%20lighting%2C%20cinematic%20photography&width=600&height=400&seq=5&orientation=landscape",
      gradient: "from-purple-500 to-pink-600"
    },
    {
      title: "Education System Overhaul",
      client: "Educational Empire",
      result: "80% efficiency breakthrough",
      impact: "10,000+ students transformed",
      image: "https://readdy.ai/api/search-image?query=Revolutionary%20educational%20management%20system%20with%20holographic%20student%20data%20visualization%2C%20futuristic%20African%20classroom%20with%20advanced%20technology%2C%20purple%20and%20blue%20interface%20design%2C%20educational%20transformation%20environment&width=600&height=400&seq=6&orientation=landscape",
      gradient: "from-blue-500 to-cyan-600"
    },
    {
      title: "Fintech Disruption",
      client: "Financial Revolutionary",
      result: "1M+ users in 3 months",
      impact: "Industry paradigm shift",
      image: "https://readdy.ai/api/search-image?query=Futuristic%20mobile%20banking%20app%20with%20holographic%20financial%20interfaces%2C%20African%20user%20experiencing%20revolutionary%20fintech%20design%2C%20purple%20and%20blue%20neon%20lighting%2C%20advanced%20financial%20technology%2C%20cyberpunk%20aesthetic&width=600&height=400&seq=7&orientation=landscape",
      gradient: "from-green-500 to-emerald-600"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuestion((prev) => (prev + 1) % philosophicalQuestions.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "TheSpire Revolutionary Software Development",
    "description": "Transform ideas into digital reality with revolutionary software development that doesn't just work — it dominates markets and changes industries.",
    "url": `${import.meta.env.VITE_SITE_URL || "https://thespire.com.ng"}/software`,
    "provider": {
      "@type": "Organization",
      "name": "TheSpire",
      "url": `${import.meta.env.VITE_SITE_URL || "https://thespire.com.ng"}`,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Abuja",
        "addressCountry": "Nigeria"
      }
    },
    "serviceType": "Revolutionary Software Development",
    "areaServed": {
      "@type": "Country",
      "name": "Nigeria"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Software Development Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Digital Product Revolution",
            "description": "Revolutionary React & React Native development with scalable backends"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Experience Design Mastery",
            "description": "Psychology-driven user research and conversion-optimized design"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Infrastructure Domination",
            "description": "AWS & Azure mastery with self-healing CI/CD pipelines"
          }
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      <SEO
        title="Revolutionary Software Development - TheSpire Digital Transformation"
        description="Transform ideas into digital reality with revolutionary software development that doesn't just work — it dominates markets and changes industries."
        keywords="revolutionary software development, digital transformation, web development mastery, mobile app domination, nigeria software company"
        structuredData={structuredData}
      />
      
      {/* Dynamic Background */}
      <div 
        className="fixed inset-0 opacity-30 pointer-events-none"
        style={{
          background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(109, 3, 244, 0.3) 0%, rgba(0, 127, 255, 0.2) 50%, transparent 70%)`
        }}
      />

      {/* Floating Particles */}
      <div className="fixed inset-0 pointer-events-none">
        {[...Array(25)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-purple-400 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      {/* Revolutionary Hero */}
      <section className="relative min-h-screen flex items-center justify-center pt-16 sm:pt-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center relative z-10 w-full">
          {/* Philosophical Question */}
          <div className="mb-6 sm:mb-8 h-12 sm:h-16 flex items-center justify-center">
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-purple-300 italic transition-all duration-1000 transform px-4 text-center">
              {philosophicalQuestions[currentQuestion]}
            </p>
          </div>

          {/* Main Title */}
          <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-8xl xl:text-9xl font-black mb-6 sm:mb-8 leading-none">
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 animate-pulse">
              BUILD THE
            </span>
            <span className="block text-white mt-2 sm:mt-4">
              IMPOSSIBLE
            </span>
          </h1>

          <div className="max-w-4xl mx-auto mb-8 sm:mb-12 px-4">
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300 leading-relaxed">
              We don't just develop software — 
              <span className="text-purple-400 font-semibold"> we architect digital revolutions.</span>
              <br />
              <span className="text-blue-400 font-semibold">Transform ideas into industry-dominating reality.</span>
            </p>
          </div>

          {/* Revolutionary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 mb-8 sm:mb-12 px-4">
            <div className="text-center">
              <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black text-purple-400 mb-2">100+</div>
              <div className="text-xs sm:text-sm text-gray-400">Digital Revolutions</div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black text-blue-400 mb-2">10M+</div>
              <div className="text-xs sm:text-sm text-gray-400">Lives Impacted</div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black text-pink-400 mb-2">500%</div>
              <div className="text-xs sm:text-sm text-gray-400">Average Growth</div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black text-cyan-400 mb-2">∞</div>
              <div className="text-xs sm:text-sm text-gray-400">Possibilities Created</div>
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center px-4">
            <button className="group relative px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full font-bold text-sm sm:text-base lg:text-lg overflow-hidden transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer w-full sm:w-auto">
              <span className="relative z-10">Start Your Revolution</span>
              <div className="absolute inset-0 bg-gradient-to-r from-pink-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </button>
            <button className="px-6 sm:px-8 py-3 sm:py-4 border-2 border-purple-400 rounded-full font-bold text-sm sm:text-base lg:text-lg hover:bg-purple-400 hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer w-full sm:w-auto">
              Witness the Magic
            </button>
          </div>
        </div>

        {/* Geometric Shapes */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-16 h-16 sm:w-32 sm:h-32 border border-purple-500/30 rotate-45 animate-spin-slow"></div>
          <div className="absolute bottom-1/4 right-1/4 w-12 h-12 sm:w-24 sm:h-24 border border-blue-500/30 rotate-12 animate-bounce"></div>
          <div className="absolute top-1/2 right-1/3 w-8 h-8 sm:w-16 sm:h-16 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-full animate-pulse"></div>
        </div>
      </section>

      {/* Revolutionary Services */}
      <section className="py-12 sm:py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400 mb-4 sm:mb-6">
              OUR ARSENAL
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-3xl mx-auto">
              Revolutionary services that don't just deliver — they dominate
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className="space-y-4">
              {services.map((service, index) => (
                <div
                  key={index}
                  className={`group relative p-4 sm:p-6 rounded-3xl cursor-pointer transition-all duration-500 overflow-hidden ${
                    activeService === index
                      ? 'bg-gradient-to-r from-purple-600/20 to-blue-600/20 border-2 border-purple-500/50 scale-105'
                      : 'bg-gray-800/30 hover:bg-gray-700/30 border border-gray-600/30'
                  }`}
                  onClick={() => setActiveService(index)}
                >
                  {activeService === index && (
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-blue-600/10 animate-pulse"></div>
                  )}
                  
                  <div className="relative z-10 flex items-start space-x-3 sm:space-x-4">
                    <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                      activeService === index 
                        ? `bg-gradient-to-r ${service.gradient}` 
                        : 'bg-gray-700'
                    }`}>
                      <i className={`${service.icon} text-lg sm:text-2xl text-white`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-base sm:text-lg lg:text-xl font-bold text-white">{service.title}</h3>
                        <span className={`text-xs px-2 py-1 rounded-full font-bold ${
                          activeService === index 
                            ? `bg-gradient-to-r ${service.gradient} text-white` 
                            : 'bg-gray-700 text-gray-300'
                        }`}>
                          {service.intensity}
                        </span>
                      </div>
                      <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
                        {service.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-3xl blur-xl"></div>
              <div className="relative bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur-sm p-6 sm:p-8 rounded-3xl border border-gray-700/50">
                <div className="mb-6">
                  <div className={`w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r ${services[activeService].gradient} rounded-3xl flex items-center justify-center mb-4`}>
                    <i className={`${services[activeService].icon} text-2xl sm:text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-3">
                    {services[activeService].title}
                  </h3>
                  <p className="text-gray-300 mb-6 leading-relaxed text-sm sm:text-base">
                    {services[activeService].description}
                  </p>
                </div>
                
                <div className="space-y-3 sm:space-y-4">
                  {services[activeService].features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-gradient-to-r ${services[activeService].gradient}`}></div>
                      <span className="text-gray-300 text-sm sm:text-base">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Revolutionary Case Studies */}
      <section className="py-12 sm:py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400 mb-4 sm:mb-6">
              DIGITAL DOMINATION
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-300">
              Real revolutions. Real results. Real impact.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {caseStudies.map((study, index) => (
              <div key={index} className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur-sm border border-gray-700/50 hover:scale-105 transition-all duration-500">
                <div className="relative overflow-hidden">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-32 sm:h-40 lg:h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                  
                  {/* Impact Badge */}
                  <div className={`absolute top-3 sm:top-4 right-3 sm:right-4 px-2 sm:px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r ${study.gradient} text-white`}>
                    Revolutionary
                  </div>
                </div>
                
                <div className="p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-2">{study.title}</h3>
                  <p className="text-gray-400 mb-3 text-sm sm:text-base">{study.client}</p>
                  <div className={`text-lg sm:text-xl lg:text-2xl font-black bg-gradient-to-r ${study.gradient} bg-clip-text text-transparent mb-2`}>
                    {study.result}
                  </div>
                  <p className="text-gray-300 text-xs sm:text-sm">{study.impact}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Revolutionary CTA */}
      <section className="py-12 sm:py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/50 to-blue-900/50"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-black text-white mb-6 sm:mb-8 leading-tight">
            READY TO BUILD
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">
              THE IMPOSSIBLE?
            </span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-300 mb-8 sm:mb-12">
            Your revolution starts with a single conversation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
            <Link to="/contact" className="w-full sm:w-auto">
              <button className="group relative px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full font-bold text-sm sm:text-base lg:text-lg overflow-hidden transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer w-full">
                <span className="relative z-10">Start Your Revolution</span>
                <div className="absolute inset-0 bg-gradient-to-r from-pink-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
            </Link>
            <button className="px-6 sm:px-8 py-3 sm:py-4 border-2 border-purple-400 rounded-full font-bold text-sm sm:text-base lg:text-lg hover:bg-purple-400 hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer w-full sm:w-auto">
              Schedule a Revolution Call
            </button>
          </div>
        </div>
      </section>

      {/* WhatsApp Floating Button */}
      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50">
        <a
          href="https://wa.me/2349167710173"
          target="_blank"
          rel="noopener noreferrer"
          className="w-12 h-12 sm:w-14 sm:h-14 bg-green-500 rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 transition-colors group"
        >
          <i className="ri-whatsapp-line text-white text-lg sm:text-xl lg:text-2xl group-hover:scale-110 transition-transform"></i>
        </a>
      </div>
    </div>
  );
}
